/**
 * The pure model of the Inbox page (1.N-1 deliverables 1, 2, 4 and 5): the search parameter, the
 * flattened list, the counts and the debounce. No React and no network.
 */

import type { AppEventType } from '@codex/contracts';
import {
  INBOX_CATEGORIES,
  type InboxCategoryKey,
  type InboxCountsV1,
  type InboxItemV1,
} from '@codex/contracts';

export interface InboxSearch {
  readonly category?: InboxCategoryKey | undefined;
}

/** `?category=` kept only when it names a category; anything else is dropped. */
export function parseInboxSearch(raw: Record<string, unknown>): InboxSearch {
  // `category` is always named, `undefined` when rejected: TanStack Router spreads this answer
  // over the raw search, so a key left out would keep the raw value in the route's search.
  return { category: undefined, ...pickCategory(raw.category) };
}

function pickCategory(value: unknown): InboxSearch {
  if (typeof value !== 'string') return {};
  const row = INBOX_CATEGORIES.find((category) => category.key === value);
  return row === undefined ? {} : { category: row.key };
}

/** A row's key: its category and subject. */
export function rowKey(item: Pick<InboxItemV1, 'category' | 'subjectId'>): string {
  return `${item.category}:${item.subjectId}`;
}

/** The pages flattened in the server's order, a row listed twice kept once (its first place). */
export function inboxRows(
  pages: readonly { readonly items: readonly InboxItemV1[] }[],
): InboxItemV1[] {
  const seen = new Set<string>();
  const rows: InboxItemV1[] = [];
  for (const page of pages) {
    for (const item of page.items) {
      const key = rowKey(item);
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push(item);
    }
  }
  return rows;
}

export function totalOpen(counts: InboxCountsV1 | undefined): number {
  return counts === undefined ? 0 : counts.counts.reduce((sum, row) => sum + row.count, 0);
}

/** The badge's text: empty at 0, "99+" above 99. */
export function badgeText(n: number): string {
  if (n <= 0) return '';
  return n > 99 ? '99+' : String(n);
}

export function badgeLabel(n: number): string {
  if (n <= 0) return 'No open items';
  return n === 1 ? '1 open item' : `${n} open items`;
}

export function categoryLabel(key: InboxCategoryKey): string {
  return INBOX_CATEGORIES.find((category) => category.key === key)?.label ?? key;
}

export interface VisibleCategory {
  readonly key: InboxCategoryKey;
  readonly label: string;
  readonly count: number;
}

/** Categories with a count above 0 in table order, plus the selected one even at 0. */
export function visibleCategories(
  counts: InboxCountsV1 | undefined,
  selected: InboxCategoryKey | undefined,
): VisibleCategory[] {
  const countOf = (key: InboxCategoryKey) =>
    counts?.counts.find((row) => row.category === key)?.count ?? 0;
  return INBOX_CATEGORIES.map((row) => ({
    key: row.key,
    label: row.label,
    count: countOf(row.key),
  })).filter((row) => row.count > 0 || row.key === selected);
}

/** Events that can change what the Inbox holds (1.N-3 publishes `inbox.counts` after its own writes). */
export const INBOX_REFRESH_EVENTS = [
  'doc.changed',
  'doc.lifecycle',
  'links.changed',
  'ledger.changed',
  'chrono.changed',
] as const satisfies readonly AppEventType[];

/** `fn` once after `ms` of quiet; calls inside the window restart it. `cancel` drops a pending call. */
export function debounced(fn: () => void, ms: number): (() => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const run = () => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, ms);
  };
  run.cancel = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  return run;
}
